import { execute } from '@lib/graphql/execute'
import { graphql } from '@lib/graphql/gql'
import { GetCustomerOrdersQueryVariables } from '@lib/graphql/graphql'
import { QueryConfig } from '@lib/react-query'
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

const customerOrdersQueryDocument = graphql(`
  query GetCustomerOrders($first: Int = 10, $after: String) {
    customer {
      orders(first: $first, after: $after, sortDirection: DESC) {
        edges {
          node {
            id
            orderNumber
            externalReference
            createdAt
            orderState
            paymentState
            shipmentState

            lineItems {
              edges {
                node {
                  productSlug
                  productName
                  variantName
                  sku
                  variant {
                    image {
                      src
                    }
                  }
                  quantity
                }
              }
            }
            total {
              fractionDigits
              currencyCode
              centAmount
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

export const getCustomerOrders = ({
  variables,
}: {
  variables: GetCustomerOrdersQueryVariables
}) => {
  return execute(customerOrdersQueryDocument, variables)
}

export const getInfiniteCustomerOrdersQueryOptions = ({
  variables,
}: { variables?: GetCustomerOrdersQueryVariables } = {}) => {
  return infiniteQueryOptions({
    queryKey: ['customer', variables],
    queryFn: ({ pageParam = null }: { pageParam: string | null }) => {
      return getCustomerOrders({
        variables: { first: variables.first, after: pageParam },
      })
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.customer?.orders.pageInfo.hasNextPage) {
        return null
      }
      return lastPage?.customer?.orders.pageInfo.endCursor
    },
    initialPageParam: null,
  })
}

type UseInfiniteCustomerOrdersOptions = {
  queryConfig?: QueryConfig<typeof getCustomerOrders>
  variables?: GetCustomerOrdersQueryVariables
}

export const useInfiniteCustomerOrders = ({
  variables,
  queryConfig,
}: UseInfiniteCustomerOrdersOptions = {}) => {
  return useInfiniteQuery({
    ...getInfiniteCustomerOrdersQueryOptions({ variables }),
    ...queryConfig,
  })
}
